import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Search, Edit, Users, Download, FileSpreadsheet, Power, PowerOff, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { exportToCSV, flattenObject } from '@/utils/exportCSV';
import { exportToExcel, flattenObjectForExcel, formatDataForExcel } from '@/utils/exportExcel';
import BulkActionBar from '../../components/BulkActionBar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ResourcesAdminPage = () => {
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [selectedIds, setSelectedIds] = useState([]);
  const limit = 20;

  useEffect(() => {
    fetchResources();
  }, [skip, statusFilter]);

  const fetchResources = async () => {
    const token = localStorage.getItem('token');
    setLoading(true);
    try {
      const params = new URLSearchParams({ skip, limit });
      if (search) params.append('search', search);
      if (statusFilter) params.append('status', statusFilter);

      const response = await axios.get(`${API}/admin/resources?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setResources(response.data.resources);
      setTotal(response.data.total);
    } catch (error) {
      toast.error('Failed to load resources');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setSkip(0);
    fetchResources();
  };

  // Bulk selection handlers
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedIds(resources.map(item => {
        const resource = item.resource || item;
        return resource.id;
      }));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (resourceId) => {
    if (selectedIds.includes(resourceId)) {
      setSelectedIds(selectedIds.filter(id => id !== resourceId));
    } else {
      setSelectedIds([...selectedIds, resourceId]);
    }
  };

  const handleClearSelection = () => {
    setSelectedIds([]);
  };

  // Bulk action execution
  const handleBulkAction = async (action, reason) => {
    const token = localStorage.getItem('token');
    
    try {
      const response = await axios.post(
        `${API}/admin/resources/bulk-action`,
        { ids: selectedIds, action, reason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const result = response.data;
      
      if (result.success_count > 0) {
        toast.success(`Successfully ${action}d ${result.success_count} resource(s)`);
        fetchResources(); // Refresh list
      }
      
      if (result.failed_count > 0) {
        toast.error(`Failed to ${action} ${result.failed_count} resource(s)`);
      }
      
      return result;
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${action} resources`);
      throw error;
    }
  };

  return (
    <div className="p-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold" style={{ color: '#1B3240' }}>Resources Management</h1>
        <p className="text-gray-600 mt-2">Manage all consultant resources</p>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="p-6">
          <div className="flex gap-4 items-end">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
              <div className="flex gap-2">
                <Input
                  placeholder="Search by name, role, or company..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                />
                <Button onClick={handleSearch} className="btn-snow">
                  <Search className="w-4 h-4" />
                </Button>
              </div>
            </div>

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-md"
            >
              <option value="">All Status</option>
              <option value="available">Available</option>
              <option value="occupied">Occupied</option>
              <option value="on_leave">On Leave</option>
            </select>

            <Button variant="outline" onClick={() => {
              const flattenedResources = resources.map(item => {
                const resource = item.resource || item;
                return flattenObject(resource);
              });
              exportToCSV(flattenedResources, 'resources_export');
              toast.success('Resources exported to CSV successfully');
            }}>
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>

            <Button variant="outline" onClick={() => {
              const flattenedResources = resources.map(item => {
                const resource = item.resource || item;
                const company = item.company || resource.company;
                return flattenObjectForExcel({
                  ...resource,
                  company_name: company?.name
                });
              });
              const formattedResources = formatDataForExcel(flattenedResources, {
                id: 'Resource ID',
                name: 'Name',
                role: 'Role',
                seniority: 'Seniority',
                daily_rate: 'Daily Rate',
                currency: 'Currency',
                availability_status: 'Availability',
                company_name: 'Company',
                city: 'City',
                country: 'Country',
                is_activated: 'Activated',
                created_at: 'Created Date'
              });
              exportToExcel(formattedResources, `resources_export_${new Date().toISOString().split('T')[0]}`, 'Resources');
              toast.success('Resources exported to Excel successfully');
            }}>
              <FileSpreadsheet className="w-4 h-4 mr-2" />
              Export Excel
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Resources Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: '#62D84E' }}></div>
            </div>
          ) : resources.length === 0 ? (
            <div className="text-center py-12 text-gray-500">No resources found</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-6 py-3 text-left">
                      <input
                        type="checkbox"
                        checked={selectedIds.length === resources.length && resources.length > 0}
                        onChange={handleSelectAll}
                        className="w-4 h-4 rounded border-gray-300"
                      />
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Resource Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Company</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rate</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Activated</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {resources.map((item) => {
                    const resource = item.resource || item; // Support both structures
                    const company = item.company || resource.company;
                    
                    return (
                    <tr key={resource.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(resource.id)}
                          onChange={() => handleSelectOne(resource.id)}
                          className="w-4 h-4 rounded border-gray-300"
                        />
                      </td>
                      <td className="px-6 py-4">
                        <div className="font-medium text-gray-900">{resource.name}</div>
                        <div className="text-sm text-gray-500">{resource.resource_id}</div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          resource.is_freelance 
                            ? (resource.freelance_model === 'represented' 
                                ? 'bg-purple-100 text-purple-800' 
                                : 'bg-orange-100 text-orange-800')
                            : 'bg-blue-100 text-blue-800'
                        }`}>
                          {resource.is_freelance 
                            ? (resource.freelance_model === 'represented' ? 'Represented Freelance' : 'Independent Freelance')
                            : 'RM Resource'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm">{resource.role}</div>
                        <div className="text-xs text-gray-500">{resource.seniority}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm">{company?.name || 'N/A'}</div>
                        <div className="text-xs text-gray-500">{resource.city}, {resource.country}</div>
                      </td>
                      <td className="px-6 py-4 text-sm">
                        {resource.currency} {resource.daily_rate}/day
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          resource.availability_status === 'available' ? 'bg-green-100 text-green-800' :
                          resource.availability_status === 'occupied' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {resource.availability_status}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          resource.is_activated ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                          {resource.is_activated ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <Link to={`/admin/resources/${resource.id}`}>
                          <Button variant="ghost" size="sm">
                            <Edit className="w-4 h-4" />
                          </Button>
                        </Link>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      <div className="flex justify-between items-center mt-6">
        <div className="text-sm text-gray-600">
          Showing {skip + 1} to {Math.min(skip + limit, total)} of {total} resources
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => setSkip(Math.max(0, skip - limit))}
            disabled={skip === 0}
          >
            Previous
          </Button>
          <Button
            variant="outline"
            onClick={() => setSkip(skip + limit)}
            disabled={skip + limit >= total}
          >
            Next
          </Button>
        </div>
      </div>

      {/* Bulk Actions Bar */}
      <BulkActionBar
        selectedIds={selectedIds}
        onClearSelection={handleClearSelection}
        actions={[
          { label: 'Activate', value: 'activate', color: 'text-green-600 border-green-600', icon: Power },
          { label: 'Deactivate', value: 'deactivate', color: 'text-orange-600 border-orange-600', icon: PowerOff },
          { label: 'Delete', value: 'delete', color: 'text-red-600 border-red-600', icon: Trash2 }
        ]}
        onExecute={handleBulkAction}
        entityType="resources"
      />
    </div>
  );
};

export default ResourcesAdminPage;